import { forwardRef, useEffect, useImperativeHandle, useRef, useState, useCallback } from 'react';
import { ArrowLeft, Loader2, Info, Check, User, Sparkles } from 'lucide-react';
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Textarea } from "../components/ui/TextArea";
import { Card, CardContent } from "../components/ui/Card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/Select";
import { MultiSelect } from "../components/ui/MultiSelect";
import { TagManagement, TagManagementRef } from "../components/TagManagement";
import { Agent, AccessType, UserData } from '../types';
import ToolsSelect from './ToolsSelect';
import { useAuth } from '../contexts/AuthContext';
import { useUsersApi } from '../apis/users-apis';
import { Checkbox } from "../components/ui/Checkbox";
import { ADMIN_SERVICE_USER } from '../config/api-config';

interface AgentFormProps {
  agent: Agent;
  originalAgent?: Agent | null;
  onAgentChange: (agent: Agent) => void;
  onSubmit: () => Promise<void>;
  onCancel: () => void;
  isEditing?: boolean;
  tagSuggestions?: string[];
  existingAgents: Agent[];
  isSaving?: boolean;
}

export interface AgentFormRef {
  validateForm: () => Promise<boolean>;
  getErrors: () => Record<string, string>;
}

const LoadingOverlay = () => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg p-6 flex items-center space-x-4">
      <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
      <span className="text-lg font-medium">Saving Agent... Please wait</span>
    </div>
  </div>
);

const SaveOverlay = () => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg p-6 flex items-center space-x-4">
      <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
      <span className="text-lg font-medium">Saving Agent... Please wait</span>
    </div>
  </div>
);

// Step Components
const StepHeader: React.FC<{ step: number; onCancel: () => void }> = ({ step, onCancel }) => {
  const getStepTitle = () => {
    switch (step) {
      case 1: return 'Basic Information';
      case 2: return 'Agent Configuration';
      case 3: return 'Tools & Capabilities';
      case 4: return 'Access Control';
      default: return 'Agent Setup';
    }
  };

  const getStepDescription = () => {
    switch (step) {
      case 1: return 'Set up the basic details and identity of your agent';
      case 2: return 'Define your agent\'s behavior and instructions';
      case 3: return 'Choose tools and capabilities for your agent';
      case 4: return 'Configure permissions and access control';
      default: return 'Configure your intelligent agent';
    }
  };

  return (
    <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 text-white py-6 px-6 sm:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-4 mb-4">
          <Button
            variant="ghost"
            onClick={onCancel}
            className="text-white hover:bg-white/20 p-2 rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="bg-white/20 backdrop-blur-sm p-3 rounded-2xl">
            <User className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-1">
              {getStepTitle()}
            </h1>
            <p className="text-purple-100">
              {getStepDescription()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProgressIndicator: React.FC<{
  currentStep: number;
  completedSteps: Set<number>;
  totalSteps: number;
  onStepClick: (step: number) => void;
}> = ({ currentStep, completedSteps, totalSteps, onStepClick }) => {
  const getStepLabel = (stepNum: number) => {
    switch (stepNum) {
      case 1: return 'Basic Info';
      case 2: return 'Configuration';
      case 3: return 'Tools';
      case 4: return 'Access Control';
      default: return `Step ${stepNum}`;
    }
  };

  return (
    <div className="max-w-6xl mx-auto mb-8 sm:mb-12">
      <div className="flex items-center justify-between pb-2 sm:pb-0">
        {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
          <div key={step} className="flex items-center flex-shrink-0">
            <button
              onClick={() => onStepClick(step)}
              className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-semibold text-xs sm:text-sm transition-all duration-300 ${
                step === currentStep
                  ? 'bg-purple-600 text-white shadow-lg scale-110'
                  : completedSteps.has(step)
                  ? 'bg-green-500 text-white cursor-pointer hover:bg-green-600'
                  : step < currentStep
                  ? 'bg-purple-100 text-purple-600 cursor-pointer hover:bg-purple-200'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }`}
              disabled={step > currentStep && !completedSteps.has(step - 1)}
            >
              {completedSteps.has(step) ? '✓' : step}
            </button>
            <span className={`ml-2 text-xs sm:text-sm font-medium whitespace-nowrap ${
              step === currentStep
                ? 'text-purple-600'
                : completedSteps.has(step)
                ? 'text-green-600'
                : 'text-gray-500'
            }`}>
              {getStepLabel(step)}
            </span>
            {step < totalSteps && (
              <div className={`w-8 sm:w-12 h-1 mx-2 sm:mx-3 rounded-full ${
                completedSteps.has(step) ? 'bg-green-500' : 'bg-gray-200'
              }`} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const BasicInfoStep: React.FC<{
  agent: Agent;
  onInputChange: (field: keyof Agent, value: any) => void;
  errors: Record<string, string>;
  tagSuggestions: string[];
  tagManagementRef: React.RefObject<TagManagementRef | null>;
}> = ({ agent, onInputChange, errors, tagSuggestions, tagManagementRef }) => (
  <div className="space-y-6 sm:space-y-8">
    <div>
      <label className="block text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
        Agent Name <span className="text-red-500">*</span>
      </label>
      <Input
        value={agent.agentName}
        onChange={(e) => onInputChange('agentName', e.target.value)}
        placeholder="e.g., Customer Support Agent, Data Analyst, Research Assistant"
        className={`rounded-xl border-gray-200 focus:border-purple-500 focus:ring-purple-500/20 transition-all duration-300 text-sm sm:text-base py-3 sm:py-4 px-4 sm:px-6 shadow-sm hover:shadow-md ${errors.agentName ? 'border-red-300 focus:border-red-500' : ''}`}
      />
      {errors.agentName && (
        <p className="mt-2 sm:mt-3 text-red-500 text-sm font-medium">{errors.agentName}</p>
      )}
    </div>

    <div>
      <label className="block text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
        Description <span className="text-red-500">*</span>
      </label>
      <Textarea
        value={agent.agentDescription}
        onChange={(e) => onInputChange('agentDescription', e.target.value)}
        placeholder="Describe what this agent does and how it helps accomplish tasks"
        rows={4}
        className={`rounded-xl border-gray-200 focus:border-purple-500 focus:ring-purple-500/20 transition-all duration-300 resize-none text-sm sm:text-base py-3 sm:py-4 px-4 sm:px-6 shadow-sm hover:shadow-md ${errors.agentDescription ? 'border-red-300 focus:border-red-500' : ''}`}
      />
      {errors.agentDescription && (
        <p className="mt-2 sm:mt-3 text-red-500 text-sm font-medium">{errors.agentDescription}</p>
      )}
    </div>

    <div>
      <label className="block text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
        Tags <span className="text-gray-500 text-sm sm:text-base font-normal">(Optional)</span>
      </label>
      <TagManagement
        ref={tagManagementRef}
        tags={agent.agentTags}
        onChange={(newTags) => onInputChange('agentTags', newTags)}
        readOnly={false}
        suggestions={tagSuggestions}
        className="w-full"
      />
    </div>
  </div>
);

const ConfigurationStep: React.FC<{
  agent: Agent;
  onInputChange: (field: keyof Agent, value: any) => void;
  errors: Record<string, string>;
}> = ({ agent, onInputChange, errors }) => (
  <div className="space-y-6 sm:space-y-8">
    <div>
      <label className="block text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
        Agent Prompt <span className="text-red-500">*</span>
      </label>
      <p className="text-gray-600 text-sm sm:text-base mb-4 leading-relaxed">
        Define your agent's role, behavior, and instructions. Be specific about how the agent should interact and what tasks it should perform.
      </p>
      <Textarea
        value={agent.agentPrompt}
        onChange={(e) => onInputChange('agentPrompt', e.target.value)}
        placeholder="You are a helpful AI assistant specialized in..."
        rows={8}
        className={`rounded-xl border-gray-200 focus:border-purple-500 focus:ring-purple-500/20 transition-all duration-300 resize-none text-sm sm:text-base py-3 sm:py-4 px-4 sm:px-6 shadow-sm hover:shadow-md ${errors.agentPrompt ? 'border-red-300 focus:border-red-500' : ''}`}
      />
      {errors.agentPrompt && (
        <p className="mt-2 sm:mt-3 text-red-500 text-sm font-medium">{errors.agentPrompt}</p>
      )}
    </div>
  </div>
);

const ToolsStep: React.FC<{
  agent: Agent;
  onInputChange: (field: keyof Agent, value: any) => void;
}> = ({ agent, onInputChange }) => (
  <div className="space-y-6 sm:space-y-8">
    <div>
      <label className="block text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
        Tools & Capabilities <span className="text-gray-500 text-sm sm:text-base font-normal">(Optional)</span>
      </label>
      <p className="text-gray-600 text-sm sm:text-base mb-4 leading-relaxed">
        Choose tools that enhance your agent's capabilities. Tools allow your agent to interact with external systems, process data, and perform specific tasks.
      </p>
      <ToolsSelect
        selectedTools={agent.agentSelectedTools}
        onChange={(tools) => onInputChange('agentSelectedTools', tools)}
        readOnly={false}
      />
    </div>
  </div>
);

const AccessControlStep: React.FC<{
  agent: Agent;
  onInputChange: (field: keyof Agent, value: any) => void;
  errors: Record<string, string>;
  isCurrentUserAdmin: boolean;
  isCoreAgent: boolean;
  onCoreAgentChange: (checked: boolean) => void;
  isLoadingUsers: boolean;
  users: UserData[];
}> = ({ agent, onInputChange, errors, isCurrentUserAdmin, isCoreAgent, onCoreAgentChange, isLoadingUsers, users }) => (
  <div className="space-y-6 sm:space-y-8">
    {isCurrentUserAdmin && (
      <div className="bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-200 rounded-xl p-4 sm:p-6">
        <div className="flex items-center gap-3 sm:gap-4">
          <Checkbox 
            id="coreAgentToggle"
            checked={isCoreAgent}
            onCheckedChange={onCoreAgentChange}
            className="data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600"
          />
          <div>
            <label htmlFor="coreAgentToggle" className="text-base sm:text-lg font-semibold text-gray-900 cursor-pointer">
              Core Agent
            </label>
            <p className="text-gray-600 text-sm sm:text-base mt-1">
              Mark this as a core system agent (admin only)
            </p>
          </div>
        </div>
      </div>
    )}

    {!isCoreAgent && (
      <>
        <div>
          <label className="block text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
            View Access Type <span className="text-red-500">*</span>
          </label>
          <Select 
            value={agent.accessControl?.viewAccessType || ''} 
            onValueChange={(value) => {
              const newAccessControl = {
                ...agent.accessControl,
                viewAccessType: value
              };
              onInputChange('accessControl', newAccessControl);
            }}
          >
            <SelectTrigger className={`rounded-xl border-gray-200 focus:border-purple-500 focus:ring-purple-500/20 py-3 sm:py-4 text-sm sm:text-base shadow-sm hover:shadow-md ${errors.viewAccessType ? 'border-red-300 focus:border-red-500' : ''}`}>
              <SelectValue placeholder="Select view access type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All Users">All Users</SelectItem>
              <SelectItem value="Owner Only">Owner Only</SelectItem>
              <SelectItem value="Allowed Users">Allowed Users</SelectItem>
            </SelectContent>
          </Select>
          {errors.viewAccessType && (
            <p className="mt-2 sm:mt-3 text-red-500 text-sm font-medium">{errors.viewAccessType}</p>
          )}
        </div>

        {agent.accessControl?.viewAccessType === 'Allowed Users' && (
          <div>
            <label className="block text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
              Select Users for View Access
            </label>
            {isLoadingUsers ? (
              <div className="flex items-center gap-2 text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading users...
              </div>
            ) : (
              <MultiSelect
                options={users
                  .filter(user => user?.userId && user?.email)
                  .map(user => ({
                    value: user.userId,
                    label: user.email,
                    tags: [user.email, user.userId].filter(Boolean)
                  }))}
                value={agent.accessControl?.viewAllowedUsers || []}
                onChange={(selectedUsers) => {
                  const newAccessControl = {
                    ...agent.accessControl,
                    viewAllowedUsers: selectedUsers
                  };
                  onInputChange('accessControl', newAccessControl);
                }}
                placeholder="Search and select users..."
                className="rounded-xl border-gray-200 focus:border-purple-500 focus:ring-purple-500/20"
              />
            )}
          </div>
        )}

        <div>
          <label className="block text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
            Edit Access Type <span className="text-red-500">*</span>
          </label>
          <Select 
            value={agent.accessControl?.editAccessType || ''} 
            onValueChange={(value) => {
              const newAccessControl = {
                ...agent.accessControl,
                editAccessType: value
              };
              onInputChange('accessControl', newAccessControl);
            }}
          >
            <SelectTrigger className={`rounded-xl border-gray-200 focus:border-purple-500 focus:ring-purple-500/20 py-3 sm:py-4 text-sm sm:text-base shadow-sm hover:shadow-md ${errors.editAccessType ? 'border-red-300 focus:border-red-500' : ''}`}>
              <SelectValue placeholder="Select edit access type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Owner Only">Owner Only</SelectItem>
              <SelectItem value="Allowed Users">Allowed Users</SelectItem>
            </SelectContent>
          </Select>
          {errors.editAccessType && (
            <p className="mt-2 sm:mt-3 text-red-500 text-sm font-medium">{errors.editAccessType}</p>
          )}
        </div>

        {agent.accessControl?.editAccessType === 'Allowed Users' && (
          <div>
            <label className="block text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
              Select Users for Edit Access
            </label>
            {isLoadingUsers ? (
              <div className="flex items-center gap-2 text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading users...
              </div>
            ) : (
              <MultiSelect
                options={users
                  .filter(user => user?.userId && user?.email)
                  .map(user => ({
                    value: user.userId,
                    label: user.email,
                    tags: [user.email, user.userId].filter(Boolean)
                  }))}
                value={agent.accessControl?.editAllowedUsers || []}
                onChange={(selectedUsers) => {
                  const newAccessControl = {
                    ...agent.accessControl,
                    editAllowedUsers: selectedUsers
                  };
                  onInputChange('accessControl', newAccessControl);
                }}
                placeholder="Search and select users..."
                className="rounded-xl border-gray-200 focus:border-purple-500 focus:ring-purple-500/20"
              />
            )}
          </div>
        )}
      </>
    )}
  </div>
);

const NavigationButtons: React.FC<{
  currentStep: number;
  totalSteps: number;
  onPrevious: () => void;
  onNext: () => void;
  onSubmit: () => void;
  isSaving: boolean;
  isEditing: boolean;
}> = ({ currentStep, totalSteps, onPrevious, onNext, onSubmit, isSaving, isEditing }) => (
  <div className="flex justify-between items-center pt-8 mt-8 border-t border-gray-200">
    <Button
      type="button"
      variant="outline"
      onClick={onPrevious}
      disabled={currentStep === 1}
      className="border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl px-6 py-3 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <ArrowLeft className="mr-2 h-4 w-4" />
      Previous
    </Button>
    
    {currentStep < totalSteps ? (
      <Button
        type="button"
        onClick={onNext}
        className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white shadow-lg hover:shadow-xl transition-all duration-300 px-8 py-3 rounded-xl font-semibold"
      >
        Next Step
        <ArrowLeft className="ml-2 h-4 w-4 rotate-180" />
      </Button>
    ) : (
      <Button
        type="button"
        onClick={onSubmit}
        disabled={isSaving}
        className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white shadow-lg hover:shadow-xl transition-all duration-300 px-8 py-3 rounded-xl font-semibold"
      >
        {isSaving ? (
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Saving...
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Check className="h-4 w-4" />
            {isEditing ? 'Update Agent' : 'Create Agent'}
          </div>
        )}
      </Button>
    )}
  </div>
);

// Custom hook for user management and admin status
const useAgentUserManagement = (user: any, getUsers: () => Promise<UserData[]>) => {
  const [isCurrentUserAdmin, setIsCurrentUserAdmin] = useState(false);
  const [users, setUsers] = useState<UserData[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [adminServiceUserId, setAdminServiceUserId] = useState<string>('');

  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoadingUsers(true);
      try {
        const usersData = await getUsers();
        setUsers(usersData);
        
        // Check if current user is admin
        const currentUserData = usersData.find(u => u.userId === user?.sub);
        setIsCurrentUserAdmin(currentUserData?.isAdmin || false);
        
        // Find admin service account
        const adminServiceAccount = usersData.find(u => u.email === ADMIN_SERVICE_USER);
        if (adminServiceAccount) {
          setAdminServiceUserId(adminServiceAccount.userId);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setIsLoadingUsers(false);
      }
    };

    if (user?.sub) {
      fetchUsers();
    }
  }, [user?.sub]);

  return {
    isCurrentUserAdmin,
    users,
    isLoadingUsers,
    adminServiceUserId
  };
};

// Custom hook for step navigation
const useStepNavigation = (totalSteps: number, validateStep: (step: number) => boolean) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());

  const handleNextStep = () => {
    if (validateStep(currentStep)) {
      setCompletedSteps(prev => {
        const newSet = new Set(prev);
        newSet.add(currentStep);
        return newSet;
      });
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (step: number) => {
    if (step <= currentStep || completedSteps.has(step - 1)) {
      setCurrentStep(step);
    }
  };

  return {
    currentStep,
    setCurrentStep,
    completedSteps,
    setCompletedSteps,
    handleNextStep,
    handlePreviousStep,
    handleStepClick
  };
};

// Custom hook for agent configuration
const useAgentConfiguration = (agent: Agent, onAgentChange: (agent: Agent) => void, adminServiceUserId: string) => {
  const [isCoreAgent, setIsCoreAgent] = useState(agent?.isCore || false);

  // Handle core agent changes
  useEffect(() => {
    if (isCoreAgent && adminServiceUserId) {
      const updatedAgent: Agent = {
        ...agent,
        isCore: true,
        ownerId: adminServiceUserId,
        accessControl: {
          viewAccessType: 'Owner Only' as AccessType,
          viewAllowedUsers: [],
          editAccessType: 'Owner Only' as AccessType,
          editAllowedUsers: []
        }
      };
      onAgentChange(updatedAgent);
    } else if (!isCoreAgent && agent.isCore) {
      const updatedAgent: Agent = {
        ...agent,
        isCore: false
      };
      onAgentChange(updatedAgent);
    }
  }, [isCoreAgent, adminServiceUserId, agent, onAgentChange]);

  // Initialize access control if not present
  useEffect(() => {
    if (!agent.accessControl && agent.agentId) {
      onAgentChange({
        ...agent,
        accessControl: {
          viewAccessType: 'Owner Only' as AccessType,
          viewAllowedUsers: [],
          editAccessType: 'Owner Only' as AccessType,
          editAllowedUsers: []
        }
      });
    }
  }, [agent.agentId, agent.accessControl, onAgentChange]);

  const toggleCoreAgent = () => {
    setIsCoreAgent(!isCoreAgent);
  };

  return {
    isCoreAgent,
    setIsCoreAgent,
    toggleCoreAgent
  };
};

// Custom hook for form validation
const useAgentValidation = (agent: Agent, existingAgents: Agent[], isEditing: boolean) => {
  const [errors, setErrors] = useState<Record<string, string>>({});

  const checkNameExists = useCallback((name: string): boolean => {
    if (!name || !existingAgents || existingAgents.length === 0) return false;
    
    return existingAgents.some(existingAgent => {
      const isSameName = existingAgent?.agentName?.toLowerCase() === name.toLowerCase();
      
      if (isEditing && agent?.agentId) {
        const isDifferentAgent = existingAgent?.agentId !== agent.agentId;
        return isSameName && isDifferentAgent;
      } else {
        return isSameName;
      }
    });
  }, [existingAgents, agent?.agentId, isEditing]);

  const validateStep1 = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!agent.agentName?.trim()) {
      newErrors.agentName = 'Agent Name is required';
    } else if (checkNameExists(agent.agentName)) {
      newErrors.agentName = 'An agent with this name already exists';
    }
    
    if (!agent.agentDescription?.trim()) {
      newErrors.agentDescription = 'Agent Description is required';
    }
    
    setErrors(prev => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  }, [agent.agentName, agent.agentDescription, checkNameExists]);

  const validateStep2 = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!agent.agentPrompt?.trim()) {
      newErrors.agentPrompt = 'Agent Prompt is required';
    }

    setErrors(prev => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  }, [agent.agentPrompt]);

  const validateStep3 = useCallback((): boolean => {
    return true; // Tools are optional
  }, []);

  const validateStep4 = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!agent.accessControl?.viewAccessType) {
      newErrors.viewAccessType = 'View Access Type is required';
    }
    
    if (!agent.accessControl?.editAccessType) {
      newErrors.editAccessType = 'Edit Access Type is required';
    }
    
    setErrors(prev => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  }, [agent.accessControl]);

  const validateStep = useCallback((step: number): boolean => {
    switch (step) {
      case 1: return validateStep1();
      case 2: return validateStep2();
      case 3: return validateStep3();
      case 4: return validateStep4();
      default: return true;
    }
  }, [validateStep1, validateStep2, validateStep3, validateStep4]);

  const validateAllSteps = useCallback(async (): Promise<boolean> => {
    const step1Valid = validateStep1();
    const step2Valid = validateStep2();
    const step3Valid = validateStep3();
    const step4Valid = validateStep4();
    
    return step1Valid && step2Valid && step3Valid && step4Valid;
  }, [validateStep1, validateStep2, validateStep3, validateStep4]);

  return {
    errors,
    setErrors,
    validateStep,
    validateAllSteps,
    checkNameExists
  };
};

const AgentForm = forwardRef<AgentFormRef, AgentFormProps>((props, ref) => {
  const {
    agent,
    onAgentChange,
    onSubmit,
    onCancel,
    isEditing = false,
    tagSuggestions = [],
    existingAgents,
    isSaving = false
  } = props;

  const totalSteps = 4;
  const { user } = useAuth();
  const { getUsers } = useUsersApi();
  const tagManagementRef = useRef<TagManagementRef>(null);

  // Use custom hooks
  const userManagement = useAgentUserManagement(user, getUsers);
  const agentConfig = useAgentConfiguration(agent, onAgentChange, userManagement.adminServiceUserId);
  const validation = useAgentValidation(agent, existingAgents, isEditing);
  const navigation = useStepNavigation(totalSteps, validation.validateStep);

  // User management, agent configuration, and validation are now handled by custom hooks

  // Validation and navigation are now handled by custom hooks

  const handleInputChange = (field: keyof Agent, value: any) => {
    onAgentChange({
      ...agent,
      [field]: value
    });
  
    // Clear errors for the field being changed
    if (validation.errors[field]) {
      validation.setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleCoreAgentChange = (checked: boolean) => {
    agentConfig.setIsCoreAgent(checked);
  };

  useImperativeHandle(ref, () => ({
    validateForm: async () => {
      // Validate all steps
      const allValid = await validation.validateAllSteps();
      
      // Process pending tags if validation passes
      if (allValid && tagManagementRef.current) {
        try {
          await tagManagementRef.current.processPendingTags();
        } catch (error) {
          console.error('Error processing tags:', error);
        }
      }
      
      return allValid;
    },
    getErrors: () => validation.errors
  }), [validation]);

  // Handle loading state if agent is not initialized
  if (!agent) {
    return (
      <div className="w-full h-64 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-purple-500 mx-auto mb-4" />
          <p className="text-gray-500">Loading agent...</p>
        </div>
      </div>
    );
  }
  
  const renderStepContent = () => {
    switch (navigation.currentStep) {
      case 1:
        return (
          <BasicInfoStep
            agent={agent}
            onInputChange={handleInputChange}
            errors={validation.errors}
            tagSuggestions={tagSuggestions}
            tagManagementRef={tagManagementRef}
          />
        );
      case 2:
        return (
          <ConfigurationStep
            agent={agent}
            onInputChange={handleInputChange}
            errors={validation.errors}
          />
        );
      case 3:
        return (
          <ToolsStep
            agent={agent}
            onInputChange={handleInputChange}
          />
        );
      case 4:
        return (
          <AccessControlStep
            agent={agent}
            onInputChange={handleInputChange}
            errors={validation.errors}
            isCurrentUserAdmin={userManagement.isCurrentUserAdmin}
            isCoreAgent={agentConfig.isCoreAgent}
            onCoreAgentChange={handleCoreAgentChange}
            isLoadingUsers={userManagement.isLoadingUsers}
            users={userManagement.users}
          />
        );
      default:
        return null;
    }
  };
  
  return (
    <div className="w-full">
      {isSaving && <SaveOverlay />}
      
      <StepHeader step={navigation.currentStep} onCancel={onCancel} />

      <div className="w-full px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <ProgressIndicator
          currentStep={navigation.currentStep}
          completedSteps={navigation.completedSteps}
          totalSteps={totalSteps}
          onStepClick={navigation.handleStepClick}
        />

        <div className="max-w-2xl mx-auto">
          <Card className="border-0 shadow-2xl bg-white">
            <CardContent className="p-6 sm:p-8 lg:p-10">
              {renderStepContent()}
              
              <NavigationButtons
                currentStep={navigation.currentStep}
                totalSteps={totalSteps}
                onPrevious={navigation.handlePreviousStep}
                onNext={navigation.handleNextStep}
                onSubmit={onSubmit}
                isSaving={isSaving}
                isEditing={isEditing}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
});

export default AgentForm;